import React from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';

/**
 * iter78i — Inline editor for an item's landed-cost defaults.
 *
 * Stored on the Item as `landed_cost_defaults`:
 *   {
 *     "tariff": {"mode":"percent"|"flat","value": 6.1},
 *     "brokerage": {...},
 *     "gst_recoverable": {...},
 *     "gst_non_recoverable": {...},
 *     "insurance": {...}, "handling": {...},
 *     "other": [ {"label":"...", "mode":"...", "value":...}, ... ]
 *   }
 *
 * Restock dialog pre-fills its inputs from these defaults; receipts can still
 * override per shipment.
 */
const FIELDS = [
  { key: 'tariff', label: 'Tariffs' },
  { key: 'brokerage', label: 'Brokerage' },
  { key: 'gst_recoverable', label: 'GST (recoverable)' },
  { key: 'gst_non_recoverable', label: 'GST (non-rec.)' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'handling', label: 'Handling' },
];

const LandedCostDefaultsEditor = ({ value, onChange }) => {
  const defaults = value || {};
  const updateField = (key, patch) => {
    onChange({
      ...defaults,
      [key]: { ...(defaults[key] || { mode: 'flat', value: '' }), ...patch },
    });
  };
  const addOther = () => {
    onChange({
      ...defaults,
      other: [...(defaults.other || []), { label: '', mode: 'flat', value: '' }],
    });
  };
  const updateOther = (idx, patch) => {
    const next = [...(defaults.other || [])];
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...defaults, other: next });
  };
  const removeOther = (idx) => {
    onChange({ ...defaults, other: (defaults.other || []).filter((_, i) => i !== idx) });
  };

  const Mode = ({ mode, onClick, testId }) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-1.5 h-7 text-[10px] rounded-sm border font-mono font-bold ${
        mode === 'percent'
          ? 'bg-orange-500 text-white border-orange-500'
          : 'bg-white text-slate-600 border-slate-300'
      }`}
      data-testid={testId}
      title={mode === 'percent' ? 'Percent of goods value' : 'Flat dollar amount'}
    >
      {mode === 'percent' ? '%' : '$'}
    </button>
  );

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {FIELDS.map(({ key, label }) => {
          const elem = defaults[key] || { mode: 'flat', value: '' };
          return (
            <div key={key} className="space-y-0.5">
              <Label className="text-[10px] text-slate-500">{label}</Label>
              <div className="flex gap-1">
                <Input
                  type="number"
                  step="0.01"
                  placeholder={elem.mode === 'percent' ? '%' : '0.00'}
                  value={elem.value ?? ''}
                  onChange={(e) => updateField(key, { value: e.target.value })}
                  className="rounded-sm mono h-7 text-xs flex-1"
                  data-testid={`landed-default-${key}`}
                />
                <Mode
                  mode={elem.mode}
                  onClick={() => updateField(key, { mode: elem.mode === 'percent' ? 'flat' : 'percent' })}
                  testId={`landed-default-${key}-mode`}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-slate-100 pt-2">
        <div className="flex items-center justify-between mb-1">
          <Label className="text-[10px] text-slate-500 uppercase tracking-wider">Additional default lines</Label>
          <button
            type="button"
            onClick={addOther}
            className="text-[10px] px-2 h-6 rounded-sm border border-slate-300 hover:bg-slate-50"
            data-testid="landed-default-other-add"
          >
            + Add line
          </button>
        </div>
        {(defaults.other || []).map((row, idx) => (
          <div key={idx} className="grid grid-cols-[1fr,90px,32px,28px] gap-1 mb-1">
            <Input
              value={row.label || ''}
              placeholder="Label"
              onChange={(e) => updateOther(idx, { label: e.target.value })}
              className="rounded-sm h-7 text-xs"
              data-testid={`landed-default-other-label-${idx}`}
            />
            <Input
              type="number"
              step="0.01"
              value={row.value ?? ''}
              placeholder={row.mode === 'percent' ? '%' : '0.00'}
              onChange={(e) => updateOther(idx, { value: e.target.value })}
              className="rounded-sm mono h-7 text-xs"
              data-testid={`landed-default-other-value-${idx}`}
            />
            <Mode
              mode={row.mode}
              onClick={() => updateOther(idx, { mode: row.mode === 'percent' ? 'flat' : 'percent' })}
              testId={`landed-default-other-mode-${idx}`}
            />
            <button
              type="button"
              onClick={() => removeOther(idx)}
              className="text-red-500 hover:text-red-700 text-[14px]"
              data-testid={`landed-default-other-remove-${idx}`}
              title="Remove"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-500">
        These defaults pre-fill the Restock dialog. Receivers can still override per shipment.
        Percent values are applied to the goods value (qty × unit cost) at receipt time.
      </p>
    </div>
  );
};

export default LandedCostDefaultsEditor;
